import { z } from 'zod';

// ==============================|| DASH-P5 — dashboard segment breakdowns ||============================== //
// GET /api/v1/{commerce|engagement|exam}/dashboard/breakdown?measure&groupBy&splitBy&from&to&timeZone
//     &countryIds=…&schoolIds=…&programTypeIds=…&limit  (+ &currency= on Commerce)
// One measure grouped by the PARTICIPANT's country / school, or the programme type, optionally split by
// a second dimension. Each service owns its measures; the shape of a row is the same everywhere.
//   • keys are strings: an opaque id, or one of SEGMENT_KEYS — "none" (not set), "unassigned" (Commerce:
//     a teacher-bought seat nobody claimed yet), "_other" (the folded tail beyond `limit`).
//   • grouped by programme a record counts under EACH of its programmes, so rows may add up to more
//     than `totals.value` (decided 2026-09-29). Never re-derive a total by summing rows.
//   • a rate row's `count` is its denominator; the page shows "—" for a small cohort.
//   • measure / groupBy / splitBy are plain strings on the wire (a Zod enum would reject the whole
//     response the day a server adds one mid-deploy).

/**
 * DASH-P5 filter bar — what a SUMMARY did with the country / school / programme filter it was sent.
 * `notFiltered` names the sections an ACTIVE filter could not narrow; the page marks those tiles
 * "Not filtered" instead of implying they were. Optional on the wire: a server from before the filter
 * bar sends nothing, which reads as "no filter applied anywhere".
 */
export const dashboardFilterSchema = z.object({
  active: z.boolean(),
  notFiltered: z.array(z.string()),
  /**
   * DASH-ALLFLT (2026-10-16) — per section, WHICH active filters it could not apply (`country | school | programme
   * | event | ticket | registrationStatus | paymentStatus`). Absent from a summary that does not know the
   * registration filters at all (Exam, Identity, or a server from before) — read that as "applies none of them".
   */
  notApplied: z.array(z.object({ section: z.string(), filters: z.array(z.string()) })).nullish()
});
export type DashboardFilter = z.infer<typeof dashboardFilterSchema>;

export const SEGMENT_KEYS = { none: 'none', unassigned: 'unassigned', other: '_other' } as const;
export const SEGMENT_DIMENSIONS = ['country', 'school', 'programme'] as const;
export type SegmentDimension = (typeof SEGMENT_DIMENSIONS)[number];

export const segmentSplitSchema = z.object({ key: z.string(), value: z.number(), count: z.number().int() });
export const segmentRowSchema = z.object({
  key: z.string(),
  value: z.number(),
  count: z.number().int(),
  split: z.array(segmentSplitSchema)
});
export const segmentTotalsSchema = z.object({
  value: z.number(),
  count: z.number().int(),
  /** how many groups exist before folding */
  rows: z.number().int(),
  /** how many of them were folded into "_other" */
  foldedRows: z.number().int()
});

const breakdownPeriod = z.object({ from: z.string(), to: z.string(), timeZone: z.string() });

const breakdownBase = z.object({
  period: breakdownPeriod,
  measure: z.string(),
  groupBy: z.string(),
  splitBy: z.string().nullable(),
  rows: z.array(segmentRowSchema),
  totals: segmentTotalsSchema
});

export const commerceDashboardBreakdownSchema = breakdownBase.extend({
  /** the ONE currency this answer is in — money is never summed across currencies */
  currency: z.string(),
  /** every currency with money in the window, so the page can offer the others */
  currencies: z.array(z.string())
});
/**
 * DASH-XP-OWN — a registrations breakdown split by the registration's own event or ticket names the split
 * keys here (Engagement owns the names). Nullish: absent for every other split, and on a server that
 * predates it.
 */
const splitLabels = z.record(z.string(), z.string()).nullish();
/**
 * TCH-XP — names for the SCHOOL and ROLE keys of the rows and splits, filled by Engagement when the request
 * groups or splits by them. The teacher SPA can read neither the school register nor the role catalogue,
 * so the answer names its own rows. Nullish: absent otherwise, and on a server that predates it.
 */
const keyLabels = z.record(z.string(), z.string()).nullish();
export const engagementDashboardBreakdownSchema = breakdownBase.extend({ splitLabels, keyLabels });
export const examDashboardBreakdownSchema = breakdownBase.extend({ isRate: z.boolean() });

/** The common shape the Explorer renders (Commerce/Exam extras are optional). */
export const dashboardBreakdownSchema = breakdownBase.extend({
  currency: z.string().optional(),
  currencies: z.array(z.string()).optional(),
  isRate: z.boolean().optional(),
  splitLabels,
  keyLabels
});

export type SegmentRow = z.infer<typeof segmentRowSchema>;
export type SegmentTotals = z.infer<typeof segmentTotalsSchema>;
export type DashboardBreakdown = z.infer<typeof dashboardBreakdownSchema>;
